import { Crop, BgRot, SectionTitle, TickerBand } from './Shared';

/* Skeleton of the hub shown while GET /golden-quest is in flight. The static
   chrome (backdrop, logo, ticker, "Your Streaks") is real; everything the API
   decides is a shimmer block laid out at the live hub's offsets, so the page
   drops into place without a jump once the data lands.

   Every block carries its stage x as --x: the sweep highlight is a single
   375px-wide band positioned at -x inside each block, so one gold wave crosses
   the whole page in step instead of each block flickering on its own. */
function Sk({ x, y, w, h, r = 12, ox = 0, tone = '' }) {
  return (
    <i
      className={`gq-sk${tone ? ' gq-' + tone : ''}`}
      style={{ left: x, top: y, width: w, height: h, borderRadius: r, '--x': `${ox + x}px` }}
    />
  );
}

/* Gems / Chests tile: art, count, label and the status strip along the bottom */
function SkTile({ x }) {
  return (
    <>
      <Sk x={x} y={214} w={161.5} h={112} r={18} />
      <Sk x={x + 12} y={234} w={42} h={51} r={10} />
      <Sk x={x + 65} y={238} w={58} h={30} r={8} />
      <Sk x={x + 65} y={276} w={40} h={11} r={5} tone="dim" />
      <Sk x={x} y={298} w={161.5} h={28} r="0 0 18px 18px" tone="dim" />
    </>
  );
}

/* One streak card: art tile, title, two description lines + the fine line,
   the gem track with its chest and labels, and the gold CTA */
function SkCard({ y }) {
  const ox = 20; // card left edge on the stage
  return (
    <div className="gq-sk-card" style={{ top: y }}>
      <Sk ox={ox} x={6} y={12} w={88} h={88} r={22} tone="dim" />
      <Sk ox={ox} x={102} y={24} w={118} h={14} r={7} />
      <Sk ox={ox} x={101.5} y={48} w={218} h={11} r={5} tone="dim" />
      <Sk ox={ox} x={101.5} y={64} w={170} h={11} r={5} tone="dim" />
      <Sk ox={ox} x={101.5} y={84} w={150} h={10} r={5} tone="dim" />
      <i className="gq-sk-divider" />
      {[65, 134, 201, 264].map((gx) => <Sk key={gx} ox={ox} x={gx} y={143} w={18} h={22} r="50%" />)}
      <Sk ox={ox} x={299} y={142} w={26} h={25} r={7} />
      {[19.8, 62.8, 130.8, 194.8, 258.8].map((lx) => <Sk key={lx} ox={ox} x={lx} y={175} w={30} h={9} r={4} tone="dim" />)}
      <Sk ox={ox} x={20} y={210} w={296.9} h={46} r={23} tone="hi" />
    </div>
  );
}

const HERO_H = 656;      // live hub hero
const BODY_OVERLAP = 26; // .gq-body rides up over the hero by this much
const CARD_TOP = 191;    // first card's top inside the body (pill → title → rule)
const CARD_STEP = 279 + 20;

export default function Skeleton({ stageHeight }) {
  /* fill the viewport exactly: the body is clipped, so a part-visible card at
     the bottom reads as "more below" without making the loader scroll */
  const bodyH = Math.max(stageHeight - (HERO_H - BODY_OVERLAP), 200);
  const cards = Math.min(3, Math.ceil((bodyH - CARD_TOP) / CARD_STEP) + 1);

  return (
    <div className="gq-hub gq-sk-hub" role="status" aria-busy="true" aria-live="polite">
      <p className="gq-sr">Loading The Golden Quest…</p>
      <div className="gq-hub-content" aria-hidden="true">
        <div className="gq-hero">
          <BgRot />
          <div className="gq-topscrim" />

          {/* top bar: back · How to play · share */}
          <Sk x={21} y={22} w={23} h={20} r={5} tone="dim" />
          <Sk x={227} y={17} w={88} h={30} r={15} />
          <Sk x={331} y={19} w={24} h={26} r={6} tone="dim" />

          <Crop kind="logo" className="gq-logo" />
          {/* two-line subtitle */}
          <Sk x={47.5} y={162} w={280} h={12} r={6} />
          <Sk x={87.5} y={179} w={200} h={12} r={6} />

          <SkTile x={19} />
          <SkTile x={192.5} />

          {/* prize podium artwork and "View all prizes" */}
          <Sk x={40} y={350} w={79} h={74} r={20} tone="dim" />
          <Sk x={133} y={336} w={109} h={71} r={20} tone="dim" />
          <Sk x={270} y={377} w={58} h={53} r={16} tone="dim" />
          <Sk x={23} y={440} w={333} h={100} r="110px 110px 18px 18px" tone="dim" />
          <Sk x={131.5} y={550} w={112} h={26} r={13} tone="hi" />
        </div>

        <div className="gq-body gq-sk-body" style={{ height: bodyH }}>
          <TickerBand />
          <Sk x={85.5} y={50} w={204} h={32} r={16} />
          <SectionTitle variant="streaks">Your Streaks</SectionTitle>
          <Sk x={37.5} y={142.5} w={300} h={11} r={5} tone="dim" />
          <Sk x={97.5} y={158.5} w={180} h={11} r={5} tone="dim" />
          {Array.from({ length: cards }, (_, i) => <SkCard key={i} y={CARD_TOP + i * CARD_STEP} />)}
        </div>
      </div>
    </div>
  );
}
